import type React from 'react'
import styled from 'styled-components'

import type { AdditionalSubject } from '@/types/Activity'
import { OverallOutcomeStatus } from '@/types/Outcome'
import { capitalize } from '@/utils/helpers'
import { fadeIn, Colours } from '@/utils/styles'
import { isAccountant, isEmployer, isLandlord } from '@/utils/subjectHelpers'

import RouterLink from './RouterLink'

interface Props {
  people: AdditionalSubject[]
}

const Container = styled.div`
  padding-bottom: 30px;

  p {
    text-transform: capitalize;
  }
`

const Subject = styled.img`
  width: 40px;
  height: 40px;
  position: relative;
  overflow: hidden;
  animation: ${fadeIn} ease 0.5s;

  border-radius: 50%;
`

const SubjectTypeIcon = styled.img`
  width: 24px;
  height: 24px;
`

const SubjectContainer = styled.div`
  display: grid;
  grid-template-columns: 50px 1fr 30px;
  align-items: center;
  grid-gap: 10px;
  padding-top: 10px;

  &:hover {
    cursor: pointer;
  }

  #subject-type {
    color: ${Colours.teal};
  }
`

const IconAvatar = styled.div`
  background: #34bebd;
  color: white;
  border-radius: 100%;
  margin: 0;
  text-align: center;
  box-sizing: content-box;
  height: 40px;
  width: 40px;
  display: flex;
  justify-content: center;
  align-items: center;

  span {
    font-size: 20px;
  }
`

const attachedSubjects: React.FC<Props> = ({ people }) => {
  if (!people) {
    return <p>Nothing to show here!</p>
  }

  function avatarInitials(name: string): string {
    if (name.split(' ').length === 1) {
      return name.split('')[0].toUpperCase()
    }
    if (name.trim() === '') {
      return ''
    }
    return `${name.split(' ')[0][0]}${name.split(' ')[1][0]}`
      .replace('undefined', ' ')
      .toUpperCase()
  }

  return (
    <Container>
      {people.map((sub, index) => (
        <RouterLink
          key={index}
          to={
            isEmployer(sub.type)
              ? `/employer-referee/${sub.subjectId}`
              : isLandlord(sub.type)
                ? `/landlord-referee/${sub.subjectId}`
                : isAccountant(sub.type)
                  ? `/accountant-referee/${sub.subjectId}`
                  : `/applicant/${sub.subjectId}`
          }
        >
          <SubjectContainer>
            {sub.avatar ? (
              <Subject key={index} src={sub.avatar} />
            ) : (
              <IconAvatar>
                <span>{avatarInitials(sub.name)}</span>
              </IconAvatar>
            )}

            <p id="subject-name">
              {capitalize(sub.name)}
              {sub.outcome?.status === OverallOutcomeStatus.cancelled
                ? ' (Inactive)'
                : ''}
            </p>

            <SubjectTypeIcon src={`/assets/svg/${sub.type}.svg`} />
          </SubjectContainer>
        </RouterLink>
      ))}
    </Container>
  )
}

export default attachedSubjects
